/**
 * Phase 98 (98-03, D-03): the currency is an argument, not a constant.
 *
 * Every helper below defaults to `'CZK'` because the billing ledger is
 * CZK-denominated by construction — `Entry.billing_rate` / `total_billing`
 * carry no currency column, and foreign rates are converted to CZK at write
 * time and again at period close (see `docs/private/adr/0003-czk-denominated-billing-ledger.md`).
 * A caller must pass a currency only when it genuinely has one on its DTO.
 *
 * WARNING (98-RESEARCH.md Pitfall 3): never pass an organisation-level
 * display currency next to a figure that came from the CZK ledger — that
 * mislabels real money. `default_currency` (Plan 98-05) is scoped to new
 * project creation, not to relabeling stored figures.
 */
import { formatDate, formatDateTime } from './dateFormat';
import { formatNumber, getActiveNumberLocale } from './localeFormat';

/**
 * Returns the display suffix for a currency code. Czech Koruna keeps its
 * `Kč` glyph — that is what every existing surface renders and changing it
 * would be a cosmetic regression across 151 call sites. Any other currency
 * renders as its ISO code, matching `BalicekHeroCard.tsx`'s established
 * pattern rather than inventing a symbol map.
 */
export function currencySuffix(currency?: string | null): string {
  if (!currency || currency === 'CZK') return 'Kč';
  return currency;
}

/**
 * Format a number with an explicit currency suffix. Exists so a caller that
 * needs exact fraction-digit control (e.g. the cost-summary tables) gets it
 * without going through `formatCurrency`'s `showDecimals` heuristic, which
 * would change today's rendered output.
 */
export function formatMoney(
  value: number,
  currency?: string | null,
  options?: Intl.NumberFormatOptions
): string {
  // MKS-314: the separator comes from the instance locale, not a `cs-CZ`
  // literal. The suffix is unchanged — which currency a figure is in is a
  // property of the figure, never of the viewer's locale.
  return `${formatNumber(value, options)} ${currencySuffix(currency)}`;
}

/**
 * Format a number as currency (defaults to Czech Koruna, CZK, because the
 * billing ledger is CZK-denominated by construction — see file header).
 */
export function formatCurrency(value: number, showDecimals = true, currency = 'CZK'): string {
  const hasDecimals = value % 1 !== 0;
  return formatMoney(value, currency, {
    minimumFractionDigits: showDecimals && hasDecimals ? 2 : 0,
    maximumFractionDigits: showDecimals ? 2 : 0,
  });
}

/**
 * Format a rate (e.g., hourly rate) in the given currency (defaults to CZK —
 * see file header).
 */
export function formatRate(value: number | null | undefined, currency = 'CZK'): string {
  if (value == null) return '-';
  return formatCurrency(value, true, currency);
}

/**
 * Format a nullable number as currency (defaults to Czech Koruna, CZK — see
 * file header), always showing 2 decimal places. Returns '-' for
 * null/undefined values.
 *
 * Never throws (code review WR-03, 98-REVIEW.md): unlike `formatMoney`/
 * `formatCurrency` — which only use `currency` to pick a display suffix and
 * never pass it to `Intl`'s `currency` style — this helper passes `currency`
 * straight into `Intl.NumberFormat({ style: 'currency', currency })`, which
 * throws a `RangeError` for any value that is not a well-formed ISO 4217
 * code. Every other formatter in this file is documented to "never throw";
 * a caller passing a loosely-typed currency (e.g. a project's
 * `balicekCurrency`/`tmBillingCurrency` read as a bare `string` before a
 * narrower type is enforced) would otherwise crash the render instead of
 * degrading gracefully. Falls back to `formatMoney`'s suffix-based
 * rendering (2 decimal places, matching this function's own contract) on
 * any invalid currency code.
 */
export function formatCurrencyStrict(
  value: number | null | undefined,
  currency = 'CZK'
): string {
  if (value == null) return '-';
  try {
    // MKS-314: the LOCALE is the instance's, the CURRENCY is the figure's.
    // A bad locale is caught by the same `catch` that already guards a bad
    // currency code — both are a `RangeError` from this constructor, and both
    // must degrade to the suffix rendering rather than crash the render.
    return new Intl.NumberFormat(getActiveNumberLocale(), {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  } catch {
    return formatMoney(value, currency, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }
}

/**
 * Format hours: '0' for zero (consistent with money cells), one decimal place otherwise.
 * Aligns BillabilityReportPage, InvoicingStatusReportPage, and project stats displays.
 */
export function formatHours(value: number): string {
  return value === 0 ? '0' : value.toFixed(1);
}

/**
 * Format an FTE value (0.01..99.99) with up to 2 decimal places, trailing
 * zeros trimmed.
 *
 * Phase 34 (D-06): the user's verbatim ask was
 * "klidně se může zobrazovat 1 a ne 1,00 ale aby bylo možné zadat
 * třeba 0,5, nebo 0,05" — render `1` (not `1.00`), `0.5` (not `0.50`),
 * `0.05` literally. Decimal separator is `.` to match every existing
 * capacity surface (PlanningMatrix, CapacityOverview, etc.).
 *
 * Single source of truth for every capacity-display call site:
 * PlanningMatrix tile labels, CapacityOverview status row, HiringGapsSummary,
 * ProjectsCapacitySummary, ProjectCapacityGrid headcount + remaining-FTE
 * labels, lead headcount display.
 */
export function formatFte(value: number): string {
  // Guard against NaN / Infinity which toFixed passes through as string "NaN".
  // Matches the em-dash placeholder convention used by formatName (WR-01 fix).
  if (!Number.isFinite(value)) return '—';
  // toFixed(2) gives a stable 2-decimal string. parseFloat re-strips
  // trailing zeros and the otherwise-lost `.0`. Equivalent to
  // Number(x.toFixed(2)).toString() but a hair clearer.
  return parseFloat(value.toFixed(2)).toString();
}

/**
 * Format a percentage
 */
export function formatPercent(value: number): string {
  return value.toFixed(1) + '%';
}

/**
 * Parse a numeric string from a locale-aware text input. Czech keyboards send
 * `,` as the decimal separator; raw `parseFloat("0,01")` stops at the comma
 * and returns 0, while `parseFloat("01")` (some browsers strip the comma when
 * normalizing the displayed value of `<input type="number">`) returns 1 —
 * both surface as "saved as 1" or "saved as 0" in the capacity grid.
 *
 * Companion to formatFte for input-side parsing. Returns 0 for empty / non-
 * numeric input so callers can use the same `fte=0 means delete` semantic
 * the upsertNeeds endpoint expects.
 */
export function parseLocaleNumber(value: string): number {
  const normalized = value.replace(',', '.').trim();
  const n = parseFloat(normalized);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Format a user's name as "LastName FirstName". Falls back to "—" when both are null.
 */
export function formatName(user: { firstName: string | null; lastName: string | null }): string {
  return [user.lastName, user.firstName].filter(Boolean).join(' ') || '—';
}

/**
 * Format a Date as a YYYY-MM-DD string using local time.
 */
export function formatLocalDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Format an ISO date string (YYYY-MM-DD or full ISO) for display — `20.11.2026`
 * under MKS-351's European spec. Date-only inputs are parsed as calendar dates
 * to avoid timezone shifting: `@db.Date` values arrive as midnight-UTC ISO
 * strings, so reading them through `new Date(iso).getDate()` would roll back a
 * day in timezones behind UTC. Returns '-' for null/empty/unparseable input.
 *
 * MKS-351 moved the actual formatting into `utils/dateFormat.ts`, the single
 * source of truth for date order and separator. This stays as the name ~100
 * call sites already import; new code can use either.
 */
export function formatDisplayDate(value: string | null | undefined): string {
  return formatDate(value);
}

/**
 * Format an ISO timestamp for display including the time component —
 * `20.11.2026 14:30`, 24-hour. Unlike {@link formatDisplayDate} the value is a
 * real instant, so it is rendered in the viewer's local time. Returns '-' for
 * null/empty/unparseable input. See {@link formatDisplayDate} on where the
 * format itself lives.
 */
export function formatDisplayDateTime(value: string | null | undefined): string {
  return formatDateTime(value);
}

/**
 * 260723 — staleness thresholds, in days quiet. Phase 86 (86-03) lifted these
 * out of `taskStalenessEmoji`'s inline literals into named constants shared
 * with `taskRotZone` below — two independent day-number ladders is exactly
 * the drift the "Compost" redesign's phase context calls out. Do not restate
 * `5` / `15` as bare literals anywhere else; read these constants instead.
 */
export const TASK_STALE_THRESHOLD_DAYS = 5;
export const TASK_GRAVEYARD_THRESHOLD_DAYS = 15;
/** Phase 86 — the "warm" / "fresh" boundary the Compost zone model adds. */
export const TASK_WARM_THRESHOLD_DAYS = 1;

/**
 * 260913-jpy — per-project override of the two day-count thresholds above.
 * `enabled` gates whether `taskStalenessEmoji` renders anything at all for a
 * given project; the two day counts, when enabled, replace the global
 * `TASK_STALE_THRESHOLD_DAYS` / `TASK_GRAVEYARD_THRESHOLD_DAYS` ladder for
 * that project's Board view, List view, and task detail slide-over only.
 * `taskRotZone` and the Compost zone model deliberately stay on the global
 * constants — see `taskStalenessEmoji`'s JSDoc.
 */
export interface TaskStalenessConfig {
  enabled: boolean;
  staleDays: number;
  graveyardDays: number;
}

/**
 * The always-on global ladder — used as `taskStalenessEmoji`'s default
 * `config` so callers that pass no option (MyTaskRow, and any test written
 * before this change) keep exactly today's behavior. Built from the shared
 * constants above, never from fresh numeric literals.
 */
export const GLOBAL_TASK_STALENESS_CONFIG: TaskStalenessConfig = {
  enabled: true,
  staleDays: TASK_STALE_THRESHOLD_DAYS,
  graveyardDays: TASK_GRAVEYARD_THRESHOLD_DAYS,
};

/**
 * The deliberate safety default for a project surface that has not been
 * wired to a real per-project config: disabled, so the emoji stays
 * suppressed rather than silently falling back to the always-on global
 * ladder. The day counts stay at the globals so the shape is always
 * complete even while disabled.
 */
export const DISABLED_TASK_STALENESS_CONFIG: TaskStalenessConfig = {
  enabled: false,
  staleDays: TASK_STALE_THRESHOLD_DAYS,
  graveyardDays: TASK_GRAVEYARD_THRESHOLD_DAYS,
};

/**
 * Resolves a project's staleness indicator config from its three optional
 * API fields. Returns `DISABLED_TASK_STALENESS_CONFIG` when the project is
 * null/undefined (loading state, or an audience for which the project query
 * is not even enabled) so the emoji never flashes on before the real config
 * arrives. When a project is supplied, its own values win and the global
 * constants are per-field fallbacks (defensive only — the API always sends
 * all three once a project row exists).
 */
export function projectStalenessConfig(
  project:
    | {
        stalenessIndicatorEnabled?: boolean | null;
        stalenessStaleDays?: number | null;
        stalenessGraveyardDays?: number | null;
      }
    | null
    | undefined
): TaskStalenessConfig {
  if (!project) return DISABLED_TASK_STALENESS_CONFIG;
  return {
    enabled: project.stalenessIndicatorEnabled ?? false,
    staleDays: project.stalenessStaleDays ?? TASK_STALE_THRESHOLD_DAYS,
    graveyardDays: project.stalenessGraveyardDays ?? TASK_GRAVEYARD_THRESHOLD_DAYS,
  };
}

/** Pluralizes "day" — singular only when the count is exactly 1. */
function dayWord(count: number): string {
  return count === 1 ? 'day' : 'days';
}

/**
 * Days since `lastActivityAt`. Returns null for missing/unparseable input —
 * never throws. Shared by `taskStalenessEmoji` and `taskRotZone` so both read
 * the same clock.
 */
function daysQuietSince(lastActivityAt: string | null | undefined): number | null {
  if (!lastActivityAt) return null;
  const last = new Date(lastActivityAt);
  if (Number.isNaN(last.getTime())) return null;
  return (Date.now() - last.getTime()) / 86_400_000;
}

/**
 * 260723 — staleness emoji for a task, keyed off its LAST-ACTIVITY time
 * (`lastActivityAt` = max of task edit / comment / time entry). Lets internal
 * users spot neglected in-progress tasks at a glance on the kanban card, list
 * row, and detail slide-over:
 *   - no activity in > graveyardDays -> 🪦 dead/abandoned (grave)
 *   - no activity in > staleDays     -> 💩 getting stale
 *   - active within staleDays        -> null (no emoji)
 *
 * 260913-jpy — the two day counts come from `options.config`, which defaults
 * to `GLOBAL_TASK_STALENESS_CONFIG` (the always-on 5/15 global ladder) when
 * omitted. This default is what keeps MyTaskRow working UNTOUCHED — it never
 * passes a `config`, so it always gets the global ladder regardless of any
 * project's per-project override. `taskRotZone` below and the Compost zone
 * model deliberately stay on the global constants too: the two ladders no
 * longer have to agree, because one is now per-project (this function, when
 * called with a project's config) and the other is a My Tasks concept.
 * A disabled config (whether the global default or a real per-project one
 * with `enabled: false`) returns null unconditionally.
 *
 * Only applies to tasks still IN PROGRESS: pass `isTerminal: true` (Done /
 * Archived / Cancelled / ...) to suppress it entirely — no point flagging
 * finished work as stale.
 *
 * Returns null for unparseable/missing input (never throws). Callers are
 * responsible for the internal-only gate — this emoji must never render for
 * external audiences.
 */
export function taskStalenessEmoji(
  lastActivityAt: string | null | undefined,
  options?: { isTerminal?: boolean; config?: TaskStalenessConfig }
): { emoji: string; label: string } | null {
  if (options?.isTerminal) return null;
  const config = options?.config ?? GLOBAL_TASK_STALENESS_CONFIG;
  if (!config.enabled) return null;
  const daysSinceActivity = daysQuietSince(lastActivityAt);
  if (daysSinceActivity === null) return null;
  if (daysSinceActivity > config.graveyardDays) {
    return {
      emoji: '🪦',
      label: `Abandoned — no activity in over ${config.graveyardDays} ${dayWord(config.graveyardDays)}`,
    };
  }
  if (daysSinceActivity > config.staleDays) {
    return {
      emoji: '💩',
      label: `Getting stale — no activity in over ${config.staleDays} ${dayWord(config.staleDays)}`,
    };
  }
  return null;
}

/**
 * Phase 86 (86-03) — the "Compost" redesign's zone model. Thin wrapper over
 * the same clock `taskStalenessEmoji` reads, next to it by design so the two
 * never drift: terminal / graveyard (>15d) / stale (>5d) / warm (>1d) / fresh.
 *
 * A null or unparseable timestamp is treated as "fresh" — the same
 * non-rotting outcome `taskStalenessEmoji` already returns (null / no
 * emoji) for bad input. Never throws.
 */
export type TaskRotZone = 'terminal' | 'graveyard' | 'stale' | 'warm' | 'fresh';

export function taskRotZone(
  lastActivityAt: string | null | undefined,
  options?: { isTerminal?: boolean }
): TaskRotZone {
  if (options?.isTerminal) return 'terminal';
  const daysQuiet = daysQuietSince(lastActivityAt);
  if (daysQuiet === null) return 'fresh';
  if (daysQuiet > TASK_GRAVEYARD_THRESHOLD_DAYS) return 'graveyard';
  if (daysQuiet > TASK_STALE_THRESHOLD_DAYS) return 'stale';
  if (daysQuiet > TASK_WARM_THRESHOLD_DAYS) return 'warm';
  return 'fresh';
}

/**
 * Compute readable text color for a chip painted with `hex` background.
 *
 * Uses the YIQ relative-luminance formula (0.299*R + 0.587*G + 0.114*B):
 * - YIQ < 128  → return '#ffffff' (white text on dark background)
 * - YIQ >= 128 → return '#1f2937' (dark gray text on light background)
 *
 * Invalid hex (wrong length, non-hex chars) returns the dark fallback
 * '#1f2937' — the function never throws.
 *
 * Used by TaskStateChip and the per-project filter chip-row to paint
 * arbitrary admin-picked state colors with WCAG-acceptable contrast.
 * See .planning/phases/19-project-creation-task-status-chip/19-CONTEXT.md D-13.
 */
export function chipTextColor(hex: string): '#ffffff' | '#1f2937' {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex);
  if (!m) return '#1f2937';
  const n = parseInt(m[1]!, 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  const yiq = 0.299 * r + 0.587 * g + 0.114 * b;
  return yiq < 128 ? '#ffffff' : '#1f2937';
}

/**
 * Phase 49 — locked palette for SLA tier badges (CONTEXT amendment 2026-05-27).
 * No DB column exists on ProjectSlaTier; the palette is derived purely from
 * `orderIndex` (1 = highest priority). Custom color picker deferred to Phase
 * 53/54 if admin customization becomes necessary.
 *
 * Tuple is `as const` so the type system knows it's a fixed 4-element list.
 * Tests import this tuple directly to assert the locked values.
 */
export const TIER_PALETTE = ['#dc2626', '#ea580c', '#ca8a04', '#2563eb'] as const;
export const TIER_FALLBACK_COLOR = '#6b7280';

/**
 * Returns the background color for a SLA tier badge given its 1-based
 * `orderIndex`. P5+ and any out-of-range input falls back to neutral gray.
 *
 * Pair with `chipTextColor(bg)` to derive WCAG-acceptable text color
 * (mirrors TaskStateChip.tsx + TaskCard tier badge per Plan 49-04).
 */
export function getTierColor(orderIndex: number): string {
  if (!Number.isFinite(orderIndex) || orderIndex < 1) return TIER_FALLBACK_COLOR;
  const idx = Math.floor(orderIndex) - 1;
  return TIER_PALETTE[idx] ?? TIER_FALLBACK_COLOR;
}
